import React, { useState } from 'react';
import { Drawer, Form, Input, Button, App } from 'antd';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  GET_ADDRESS_RESIDENTIAL_AREA_REPORT_DETAIL,
  GetAddressResidentialAreaDetailResponse,
  AddressResidentialAreaDetailType,
  AddressResidentialAreaImageType,
} from '../graphql/queries/getAddressResidentialAreaReportDetail';
import {
    QUERY_ADDRESS_RESIDENTIAL_AREA_DETAIL_BY_HASH
} from '../graphql/queries/getAddressResidentialAreaDetail';
import { REJECT_ADDRESS_RESIDENTIAL_AREA_REPORT } from '../graphql/mutations/upsertAddressResidentialArea';
import ImageUploader from './ImageUploader';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

interface Props {
  visible: boolean;
  areaId: string | null;
  onClose: () => void;
  onRefresh?: () => void;
  /** 审核通过且正式库中不存在该小区时，打开新建窗口预填数据 */
  onApprove?: (area: AddressResidentialAreaDetailType) => void;
  /** 审核通过且正式库中已存在该小区时，打开对应小区的修改窗口，同时传入审核图片供参考和上报数据ID */
  onSwitchToEdit?: (areaId: string, reportedImages: Array<{ imageS3Key: string; imageDisplayUrl?: string | null }>, reportDataId: string) => void;
}

const DRAWER_PREFIX = 'mapTool.addressResidentialArea.drawer';
const REVIEW_PREFIX = 'mapTool.addressResidentialArea.reviewDrawer';

const AddressResidentialAreaReviewReportDrawer: React.FC<Props> = ({
  visible,
  areaId,
  onClose,
  onRefresh,
  onApprove,
  onSwitchToEdit,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [rejectLoading, setRejectLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const { data, loading } = useQuery<GetAddressResidentialAreaDetailResponse>(
      GET_ADDRESS_RESIDENTIAL_AREA_REPORT_DETAIL,
    {
      skip: !areaId || !visible,
      variables: { id: areaId ? Number(areaId) : undefined },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [rejectReport] = useMutation(REJECT_ADDRESS_RESIDENTIAL_AREA_REPORT);
  const [queryDetailByHash] = useLazyQuery<GetAddressResidentialAreaDetailResponse>(
    QUERY_ADDRESS_RESIDENTIAL_AREA_DETAIL_BY_HASH
  );

  const area = data?.addressResidentialAreaReportDetail;

  const MAX_IMAGES = 3;
  const images: AddressResidentialAreaImageType[] = area?.images ?? [];
  const slots: (AddressResidentialAreaImageType | null)[] = (() => {
    const result: (AddressResidentialAreaImageType | null)[] = Array(MAX_IMAGES).fill(null);
    const sorted = [...images].sort((a, b) => a.stopNumber - b.stopNumber);
    sorted.forEach((img) => {
      const idx = img.stopNumber - 1;
      if (idx >= 0 && idx < MAX_IMAGES) {
        result[idx] = img;
      }
    });
    return result;
  })();

  const handleReject = async () => {
    if (!areaId || !area) return;
    setRejectLoading(true);
    try {
      await rejectReport({
        variables: {
          id: Number(areaId),
        },
      });
      message.success(t(`${REVIEW_PREFIX}.messageRejectSuccess`));
      onRefresh?.();
      onClose();
    } catch (e: any) {
      message.error(e?.message || t(`${REVIEW_PREFIX}.messageFailed`));
    } finally {
      setRejectLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!area) return;
    const hash = area.residentialAddressHash;
    if (!hash) {
      onApprove?.(area);
      onClose();
      return;
    }
    setApproveLoading(true);
    try {
      const { data } = await queryDetailByHash({ variables: { residentialAddressHash: hash } });
      const existing = data?.queryAddressResidentialAreaDetail;
      if (existing?.id) {
        const imgs = area.images?.map((img) => ({ imageS3Key: img.imageS3Key, imageDisplayUrl: img.imageDisplayUrl ?? null })) ?? [];
        onSwitchToEdit?.(existing.id, imgs, areaId!);
      } else {
        onApprove?.(area);
      }
      onClose();
    } catch (e: any) {
      message.error(e?.message || t(`${REVIEW_PREFIX}.messageFailed`));
    } finally {
      setApproveLoading(false);
    }
  };

  const isPending = String(area?.status ?? '').toUpperCase() === 'PENDING';

  return (
    <Drawer
      title={t(`${REVIEW_PREFIX}.title`)}
      open={visible}
      onClose={onClose}
      width={880}
      destroyOnClose
      loading={loading}
      footer={
        <div className="flex justify-end gap-2">
          <HasResource resourceCodes={[ResourceCode.addressResidentialArea.UPDATE]}>
            {isPending && (
              <>
                <Button
                  danger
                  loading={rejectLoading}
                  onClick={handleReject}
                >
                  {t(`${REVIEW_PREFIX}.buttonRejectShort`)}
                </Button>
                <Button
                  type="primary"
                  style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                  disabled={rejectLoading}
                  loading={approveLoading}
                  onClick={handleApprove}
                >
                  {t(`${REVIEW_PREFIX}.buttonApprove`)}
                </Button>
              </>
            )}
          </HasResource>
        </div>
      }
    >
      <Form layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelResidentialAddress`)}>
          <Input value={area?.residentialAddress ?? ''} disabled />
        </Form.Item>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelStandardAddress`)}>
          <Input value={area?.standardAddress ?? ''} disabled />
        </Form.Item>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelState`)}>
          <Input value={area?.stateAbbreviation ?? ''} disabled />
        </Form.Item>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelCity`)}>
          <Input value={area?.cityName ?? ''} disabled />
        </Form.Item>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelZipcode`)}>
          <Input value={area?.zipcode ?? ''} disabled />
        </Form.Item>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelResidentialName`)}>
          <Input value={area?.residentialName ?? ''} disabled />
        </Form.Item>
        <Form.Item label={t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reportedAt')}>
          <Input
            value={area?.createdAt ? dayjs(area.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''}
            disabled
          />
        </Form.Item>
        <Form.Item label={t(`${DRAWER_PREFIX}.labelUpdatedAt`)}>
          <Input
            value={area?.updatedAt ? dayjs(area.updatedAt).format('YYYY-MM-DD HH:mm:ss') : ''}
            disabled
          />
        </Form.Item>
      </Form>

      <div className="mt-4 mb-2 font-semibold">{t(`${REVIEW_PREFIX}.sectionImagesTitle`)}</div>
      <div className="grid grid-cols-3 gap-4">
        {slots.map((slot, index) => {
          const hasImage = !!slot?.imageS3Key;
          return (
            <div key={index} className={`flex flex-col items-center min-h-[104px] ${!hasImage ? 'opacity-70' : ''}`}>
              {hasImage ? (
                <ImageUploader
                  value={slot?.imageS3Key ? [slot.imageS3Key] : []}
                  displayUrls={slot?.imageDisplayUrl ? [slot.imageDisplayUrl] : []}
                  onChange={() => {}}
                  maxCount={1}
                  maxSizeMB={25}
                  tag="address-residential-area"
                  disabled
                  usePublicBucket
                />
              ) : (
                <div className="w-full min-h-[104px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                  {t(`${DRAWER_PREFIX}.noImage`)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};

export default AddressResidentialAreaReviewReportDrawer;